import { ScrollView, TableProps } from '@round/ui-kit';
import Select from '../../../DataEntry/Select/Select';
import { RowData, Table } from '@tanstack/react-table';
import { useState } from 'react';
import WrapperTable from '../WrapperTable';
import styles from './WrapperPaginationTable.module.css';
import { StylesConfig } from 'react-select';
import { GenericDropdownOption } from '../../../../App.types';
import LeftArrowIcon from '../../../../SharedComponents/svg/Icons/LeftArrowIcon';
import RightArrowIcon from '../../../../SharedComponents/svg/Icons/RightArrowIcon';
import cn from 'classnames';

export type WrapperPaginationTableProps<TableData> = Omit<TableProps<TableData>, 'instance' | 'onPaginationChange'> & {
    count: number;
    page: number;
    setPage: (page: number) => void;
    pageSize: number;
    setPageSize: (pageSize: number) => void;
    isLoading?: boolean;
};

const pageSizeOptions: GenericDropdownOption<number>[] = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '35', value: 35 },
    { label: '50', value: 50 },
];

const pageSizeSelectStyles: StylesConfig = {
    valueContainer: (base) => ({
        ...base,
        minWidth: '2rem',
        fontWeight: 500,
    }),
    menu: (base) => ({
        ...base,
        fontSize: '0.75rem',
        fontWeight: 500,
    }),
};

const WrapperPaginationTable = <TableData extends RowData>({
    page = 1,
    setPage,
    pageSize = 10,
    setPageSize,
    count,
    isLoading,
    data,
    ...props
}: WrapperPaginationTableProps<TableData>) => {
    const [table, setTable] = useState<Table<TableData> | null>(null);

    // skeletons are rendered in table cells,
    // so we need some rows to display skeletons on initial loading
    const initialLoading = !data.length && isLoading;
    const emptyRows = new Array(pageSize || 0).fill({}) as TableData[];
    const rows = initialLoading ? emptyRows : data;

    const pageCount = Math.ceil(count / pageSize);
    const hasNextPage = page < pageCount;
    const hasPreviousPage = page > 1;

    return (
        <>
            <ScrollView>
                <WrapperTable
                    instance={setTable}
                    {...props}
                    data={rows}
                    pagination={{
                        pageIndex: page - 1,
                        pageSize: pageSize,
                    }}
                    onPaginationChange={({ pageIndex, pageSize: newPageSize }) => {
                        const newPage = pageIndex + 1;
                        if (newPage !== page) {
                            setPage(newPage);
                        }

                        if (pageSize !== newPageSize) {
                            setPageSize(newPageSize);
                        }
                    }}
                />
            </ScrollView>
            {table && (
                <div className={styles.toolbarContainer}>
                    <div className={styles.pageSizeSelectContainer}>
                        <Select
                            isSearchable={false}
                            isClearable={false}
                            styles={pageSizeSelectStyles}
                            options={pageSizeOptions}
                            value={pageSizeOptions.find((o) => o.value === pageSize)}
                            onChange={(pageSizeOption) => {
                                table.setPageSize(pageSizeOption?.value ?? 10);
                            }}
                            menuPosition="fixed"
                        />
                        <label>Records per page</label>
                    </div>

                    <div className={styles.paginationContainer}>
                        {pageCount > 0 && (
                            <span className={styles.pageLocationLabel}>
                                Page {page} of {pageCount}
                            </span>
                        )}

                        <LeftArrowIcon
                            ariaRole="button"
                            ariaLabel="Go to previous page"
                            className={cn(styles.paginationIcon, {
                                [styles.paginationIconDisabled]: !hasPreviousPage || isLoading,
                            })}
                            width={12}
                            height={12}
                            onClick={() => table.setPageIndex(page - 2)}
                        />

                        <RightArrowIcon
                            ariaRole="button"
                            ariaLabel="Go to next page"
                            className={cn(styles.paginationIcon, {
                                [styles.paginationIconDisabled]: !hasNextPage || isLoading,
                            })}
                            height={12}
                            width={12}
                            onClick={() => table.setPageIndex(page)}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default WrapperPaginationTable;
